/**
 * Developer Portal
 * API documentation, key management, and interactive examples
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Chip,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  PlayArrow as PlayIcon,
  Code as CodeIcon,
  Description as DescIcon,
  Assessment as GapIcon,
  School as PlanIcon,
  Work as RolesIcon
} from '@mui/icons-material';
import { auth } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';

// API Base URL
const API_BASE_URL = 'https://api.neurolnet.ai';

// API Endpoint definitions
const API_ENDPOINTS = [
  {
    id: 'resumes',
    name: '/resumes',
    method: 'POST',
    path: '/careerplanning/v1/resumes',
    description: 'Parse resume text into structured profile data',
    icon: DescIcon,
    request: {
      content: 'John Doe\\nSoftware Engineer at Google\\n5+ years experience...\\nSkills: Python, AWS, Kubernetes',
      format: 'text'
    },
    response: {
      profile: {
        name: 'John Doe',
        currentRole: 'Software Engineer',
        yearsExperience: 5,
        skills: ['Python', 'AWS', 'Kubernetes'],
        certifications: ['AWS Solutions Architect'],
        education: [{ degree: 'BS Computer Science', school: 'MIT', year: '2018' }],
        experience: [{ title: 'Software Engineer', company: 'Google', years: 3 }]
      },
      meta: { processingTimeMs: 1234 }
    }
  },
  {
    id: 'roles',
    name: '/roles',
    method: 'POST',
    path: '/careerplanning/v1/roles',
    description: 'Suggest target roles based on profile and career intent',
    icon: RolesIcon,
    request: {
      profile: {
        currentRole: 'Software Engineer',
        yearsExperience: 5,
        skills: ['Python', 'AWS', 'System Design']
      },
      intent: 'promotion',
      limit: 3
    },
    response: {
      suggestions: [
        {
          role: 'Senior Software Engineer',
          fit: 0.92,
          rationale: 'Natural progression with strong technical foundation',
          marketDemand: 'high',
          salaryRange: { min: 180000, max: 250000, currency: 'USD' }
        }
      ],
      meta: { processingTimeMs: 2100 }
    }
  },
  {
    id: 'gaps',
    name: '/gaps',
    method: 'POST',
    path: '/careerplanning/v1/gaps',
    description: 'Analyze skill gaps between current profile and target role',
    icon: GapIcon,
    request: {
      profile: {
        currentRole: 'Software Engineer',
        yearsExperience: 5,
        skills: ['Python', 'AWS']
      },
      targetRole: 'Engineering Manager',
      marketScope: 'US'
    },
    response: {
      readinessPercentage: 68,
      matchedSkills: [
        { skill: 'Python', relevance: 'high' },
        { skill: 'System Design', relevance: 'critical' }
      ],
      missingSkills: [
        { skill: 'People Management', priority: 1, reason: 'Required for all EM roles' }
      ],
      analysis: {
        strengths: 'Strong technical foundation...',
        gaps: 'Limited leadership experience...',
        confidence: 0.85
      }
    }
  },
  {
    id: 'plans',
    name: '/plans',
    method: 'POST',
    path: '/careerplanning/v1/plans',
    description: 'Generate personalized training plan from gap analysis',
    icon: PlanIcon,
    request: {
      profile: { currentRole: 'Software Engineer', skills: ['Python'] },
      targetRole: 'Engineering Manager',
      gaps: { missingSkills: [{ skill: 'Leadership' }] },
      constraints: {
        hoursPerWeek: 10,
        budgetCap: 500,
        learningStyle: 'video'
      }
    },
    response: {
      plan: {
        totalWeeks: 16,
        totalHours: 160,
        estimatedCost: 398,
        items: [
          {
            order: 1,
            type: 'course',
            title: 'Engineering Management Fundamentals',
            provider: 'Coursera',
            hours: 20,
            cost: 49
          }
        ]
      },
      checkpoints: [
        { week: 4, milestone: 'Complete leadership fundamentals' }
      ],
      nextAction: {
        title: 'Start Engineering Management course',
        estimatedTime: '2 hours'
      }
    }
  }
];

function DeveloperPortal() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKeys, setApiKeys] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState(API_ENDPOINTS[0]);
  const [showApiKey, setShowApiKey] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [newKeyDialog, setNewKeyDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [generatingKey, setGeneratingKey] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState(null);

  // Auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
      if (user) {
        loadApiKeys();
      }
    });
    return () => unsubscribe();
  }, []);

  // Load user's API keys
  const loadApiKeys = async () => {
    setLoadingKeys(true);
    try {
      const functions = getFunctions();
      const getKeys = httpsCallable(functions, 'getPartnerApiKeys');
      const result = await getKeys();
      setApiKeys(result.data.keys || []);
    } catch (error) {
      console.error('Failed to load API keys:', error);
      setSnackbar({ open: true, message: 'Failed to load API keys', severity: 'error' });
    }
    setLoadingKeys(false);
  };

  // Generate new API key
  const handleGenerateKey = async () => {
    setGeneratingKey(true);
    try {
      const functions = getFunctions();
      const generateKey = httpsCallable(functions, 'generatePartnerApiKey');
      const result = await generateKey({ name: newKeyName || 'API Key' });
      
      setNewKeyResult(result.data);
      setApiKeys(prev => [{
        id: 'new',
        keyPrefix: result.data.keyPrefix,
        name: newKeyName || 'API Key',
        createdAt: new Date(),
        dailyCount: 0,
        rateLimit: 100,
        status: 'active'
      }, ...prev]);
      
    } catch (error) {
      console.error('Failed to generate key:', error);
      setSnackbar({ open: true, message: 'Failed to generate API key', severity: 'error' });
      setNewKeyDialog(false);
    }
    setGeneratingKey(false);
  };

  // Revoke API key
  const handleRevokeKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This cannot be undone.')) {
      return;
    }
    
    try {
      const functions = getFunctions();
      const revokeKey = httpsCallable(functions, 'revokePartnerApiKey');
      await revokeKey({ keyId });
      
      setApiKeys(prev => prev.filter(k => k.id !== keyId));
      setSnackbar({ open: true, message: 'API key revoked', severity: 'success' });
    } catch (error) {
      console.error('Failed to revoke key:', error);
      setSnackbar({ open: true, message: 'Failed to revoke API key', severity: 'error' });
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, label = 'Copied') => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: `${label} to clipboard`, severity: 'success' });
  };

  // Generate cURL command
  const generateCurl = (endpoint) => {
    return `curl -X POST \\
  ${API_BASE_URL}${endpoint.path} \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '${JSON.stringify(endpoint.request, null, 2)}'`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#0B0C10' }}>
        <CircularProgress sx={{ color: '#00FFF5' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#0B0C10', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" sx={{ color: '#00FFF5', fontWeight: 700, mb: 1 }}>
            Developer Portal
          </Typography>
          <Typography variant="h6" sx={{ color: '#a1a1aa' }}>
            Integrate career planning capabilities into your product
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left Column - API Keys & Endpoints */}
          <Grid item xs={12} md={3} sx={{ minWidth: { md: 280 }, maxWidth: { md: 320 } }}>
            {/* API Key Management */}
            <Paper sx={{ bgcolor: '#1F2833', p: 2, mb: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#fff' }}>
                  Your API Keys
                </Typography>
                {user && (
                  <Tooltip title="Generate new key">
                    <IconButton 
                      size="small" 
                      onClick={() => { setNewKeyName(''); setNewKeyResult(null); setNewKeyDialog(true); }}
                      sx={{ color: '#00FFF5' }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {!user ? (
                <Alert severity="info" sx={{ bgcolor: 'rgba(0,255,245,0.1)' }}>
                  Sign in to generate API keys
                </Alert>
              ) : loadingKeys ? (
                <CircularProgress size={24} sx={{ color: '#00FFF5' }} />
              ) : apiKeys.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 2 }}>
                    No API keys yet
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setNewKeyDialog(true)}
                    sx={{ borderColor: '#00FFF5', color: '#00FFF5' }}
                  >
                    Generate Key
                  </Button>
                </Box>
              ) : (
                <List dense>
                  {apiKeys.filter(k => k.status === 'active').map((key) => (
                    <ListItem
                      key={key.id}
                      secondaryAction={
                        <IconButton edge="end" size="small" onClick={() => handleRevokeKey(key.id)} sx={{ color: '#ef4444' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                      sx={{ bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1, mb: 1 }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ color: '#fff', fontFamily: 'monospace' }}>
                              {key.keyPrefix}...
                            </Typography>
                            <Tooltip title="Copy prefix">
                              <IconButton size="small" onClick={() => copyToClipboard(key.keyPrefix)}>
                                <CopyIcon fontSize="small" sx={{ color: '#a1a1aa' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" sx={{ color: '#a1a1aa' }}>
                            {key.dailyCount}/{key.rateLimit} today
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>

            {/* Endpoint List */}
            <Paper sx={{ bgcolor: '#1F2833', p: 2, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
                Endpoints
              </Typography>
              <List>
                {API_ENDPOINTS.map((endpoint) => {
                  const Icon = endpoint.icon;
                  const isSelected = selectedEndpoint.id === endpoint.id;
                  return (
                    <ListItemButton
                      key={endpoint.id}
                      selected={isSelected}
                      onClick={() => setSelectedEndpoint(endpoint)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        bgcolor: isSelected ? 'rgba(0,255,245,0.1)' : 'transparent',
                        '&:hover': { bgcolor: 'rgba(0,255,245,0.05)' }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Icon sx={{ color: isSelected ? '#00FFF5' : '#a1a1aa' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ color: isSelected ? '#00FFF5' : '#fff', fontFamily: 'monospace' }}>
                            {endpoint.name}
                          </Typography>
                        }
                      />
                      <Chip
                        label={endpoint.method}
                        size="small"
                        sx={{
                          bgcolor: '#22c55e',
                          color: '#fff',
                          fontSize: '0.65rem',
                          height: 20
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>

              <Divider sx={{ my: 2, borderColor: '#333' }} />

              {/* Download OpenAPI Spec */}
              <Button
                fullWidth
                variant="outlined"
                startIcon={<DownloadIcon />}
                href="/openapi.yaml"
                download
                sx={{ borderColor: '#444', color: '#a1a1aa', '&:hover': { borderColor: '#00FFF5', color: '#00FFF5' } }}
              >
                Download OpenAPI Spec
              </Button>
            </Paper>
          </Grid>

          {/* Right Column - Documentation */}
          <Grid item xs={12} md sx={{ flex: 1, minWidth: 0 }}>
            <Paper sx={{ bgcolor: '#1F2833', p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              {/* Endpoint Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Chip
                  label={selectedEndpoint.method}
                  sx={{ bgcolor: '#22c55e', color: '#fff', fontWeight: 600 }}
                />
                <Typography variant="h5" sx={{ color: '#fff', fontFamily: 'monospace' }}>
                  {selectedEndpoint.path}
                </Typography>
              </Box>

              <Typography variant="body1" sx={{ color: '#a1a1aa', mb: 3 }}>
                {selectedEndpoint.description}
              </Typography>

              {/* Base URL */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(0,0,0,0.3)', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: '#a1a1aa' }}>BASE URL</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ color: '#00FFF5', fontFamily: 'monospace' }}>
                    {API_BASE_URL}
                  </Typography>
                  <IconButton size="small" onClick={() => copyToClipboard(API_BASE_URL, 'Base URL copied')}>
                    <CopyIcon fontSize="small" sx={{ color: '#a1a1aa' }} />
                  </IconButton>
                </Box>
              </Box>

              <Grid container spacing={3}>
                {/* Request */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: '#a1a1aa' }}>
                      REQUEST
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => copyToClipboard(JSON.stringify(selectedEndpoint.request, null, 2), 'Request copied')}
                    >
                      <CopyIcon fontSize="small" sx={{ color: '#a1a1aa' }} />
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      bgcolor: '#0B0C10',
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid #333',
                      maxHeight: 400,
                      overflow: 'auto'
                    }}
                  >
                    <pre style={{ margin: 0, color: '#00FFF5', fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(selectedEndpoint.request, null, 2)}
                    </pre>
                  </Box>
                </Grid>

                {/* Response */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: '#a1a1aa' }}>
                      RESPONSE
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => copyToClipboard(JSON.stringify(selectedEndpoint.response, null, 2), 'Response copied')}
                    >
                      <CopyIcon fontSize="small" sx={{ color: '#a1a1aa' }} />
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      bgcolor: '#0B0C10',
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid #333',
                      maxHeight: 400,
                      overflow: 'auto'
                    }}
                  >
                    <pre style={{ margin: 0, color: '#22c55e', fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(selectedEndpoint.response, null, 2)}
                    </pre>
                  </Box>
                </Grid>
              </Grid>

              {/* cURL Example */}
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: '#a1a1aa' }}>
                    cURL EXAMPLE
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => copyToClipboard(generateCurl(selectedEndpoint), 'cURL copied')}
                  >
                    <CopyIcon fontSize="small" sx={{ color: '#a1a1aa' }} />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    bgcolor: '#0B0C10',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #333',
                    overflow: 'auto'
                  }}
                >
                  <pre style={{ margin: 0, color: '#f59e0b', fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                    {generateCurl(selectedEndpoint)}
                  </pre>
                </Box>
              </Box>

              {/* Authentication Info */}
              <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(0,255,245,0.05)', borderRadius: 1, border: '1px solid rgba(0,255,245,0.2)' }}>
                <Typography variant="subtitle2" sx={{ color: '#00FFF5', mb: 1 }}>
                  Authentication
                </Typography>
                <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                  Include your API key in the <code style={{ color: '#00FFF5' }}>X-API-Key</code> header with every request.
                  Free tier includes 100 requests per day.
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* New Key Dialog */}
      <Dialog open={newKeyDialog} onClose={() => { setNewKeyDialog(false); setNewKeyResult(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1F2833', color: '#fff' }}>
          {newKeyResult ? 'API Key Generated' : 'Generate API Key'}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#1F2833' }}>
          {newKeyResult ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Save this API key now - it will not be shown again!
              </Alert>
              <Box sx={{ p: 2, bgcolor: '#0B0C10', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#00FFF5', 
                    fontFamily: 'monospace', 
                    flex: 1,
                    wordBreak: 'break-all'
                  }}
                >
                  {newKeyResult.apiKey}
                </Typography>
                <IconButton onClick={() => copyToClipboard(newKeyResult.apiKey, 'API key copied')}>
                  <CopyIcon sx={{ color: '#00FFF5' }} />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <TextField
              autoFocus
              fullWidth
              label="Key Name (optional)"
              placeholder="My Integration"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              sx={{ 
                mt: 2,
                '& .MuiOutlinedInput-root': { color: '#fff' },
                '& .MuiInputLabel-root': { color: '#a1a1aa' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' }
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#1F2833', p: 2 }}>
          <Button onClick={() => { setNewKeyDialog(false); setNewKeyResult(null); }} sx={{ color: '#a1a1aa' }}>
            {newKeyResult ? 'Done' : 'Cancel'}
          </Button>
          {!newKeyResult && (
            <Button
              variant="contained"
              onClick={handleGenerateKey}
              disabled={generatingKey}
              sx={{ bgcolor: '#00FFF5', color: '#0B0C10', '&:hover': { bgcolor: '#00CCC4' } }}
            >
              {generatingKey ? <CircularProgress size={20} /> : 'Generate'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DeveloperPortal;

